import {LitElement} from 'lit';
import {customElement, property, queryAll} from 'lit/decorators.js';
import {SbkInput} from "@/components/input/input";
import {query} from "lit/decorators/query.js";
import {SbkCheckbox} from '@/components/checkbox/checkbox';
import {SbkCheckboxGroup} from "@/components/checkbox-group/checkbox-group";
import {SbkRadioGroup} from "@/components/radio-group/radio-group";
import {SbkTextarea} from "@/components/textarea/textarea";
import {SbkSelect} from "@/components/select/select";
import "@/components/form/form.scss"
import {SbkDatepicker} from "@/components/datepicker/datepicker.ts";
import {SbkAutocompleteInputZip} from "@/components/autocomplete-input-zip/autocomplete-input-zip.ts";
import {SbkAutocompleteInputCity} from "@/components/autocomplete-input-city/autocomplete-input-city.ts";
import {SbkAutocompleteInputStreet} from "@/components/autocomplete-input-street/autocomplete-input-street.ts";
import {SbkFileUploadComponent} from "@/components/file-upload/fileupload";
import { SbkButton } from '../button/button';

declare let _paq: {
    push: (args: [string, ...unknown[]]) => void;
};

@customElement('sbk-form')
export class SbkForm extends LitElement {

    @property({attribute: 'full-width', type: Boolean})
    fullWidth: boolean = false;

    @query('form')
    _form!: HTMLFormElement

    @query('.required-hint')
    _requiredHint: HTMLElement | undefined

    @queryAll('[required]')
    _requiredElements!: HTMLElement[]

    protected createRenderRoot() {
        return this;
    }

    FORM_INPUT_EVENTS = [
        'input',
        'change',
        'reset',
        'select-value-changed',
        'radio-change',
        'datepicker-change',
        'checkbox-change',
        'checkbox-group-change',
        'file-upload-changed',
        'autocomplete-input-changed'
    ];


    connectedCallback() {
        super.connectedCallback();
        if (!this._form) return;

        this._form.addEventListener('submit', this._handleSubmit);
        this._form.addEventListener('repeatable-container-remove', this._updateRepeatableElements);
        this.FORM_INPUT_EVENTS.forEach(event =>
            this._form.addEventListener(event, this._enableSubmitButtons)
        );


        if (this._requiredHint && !this._requiredElements.length) {
            this._requiredHint.hidden = true;
        }
    }

    /*
    connectedCallback() {
        super.connectedCallback();
        this._form.addEventListener('submit', (e) => this._onSubmit(e));
        this._form.addEventListener('repeatable-container-remove', this._updateRepeatableElements);
        if (this._requiredHint && !this._requiredElements.length) {
            this._requiredHint.hidden = true;
        }
    } */

    disconnectedCallback() {
        super.disconnectedCallback();
        if (!this._form) return;

        this.FORM_INPUT_EVENTS.forEach(event =>
            this._form.removeEventListener(event, this._enableSubmitButtons)
        );

        this._form.removeEventListener('submit', this._handleSubmit);
        this._form.removeEventListener('repeatable-container-remove', this._updateRepeatableElements);
    }

    private _handleSubmit = (e: Event) => this._onSubmit(e as SubmitEvent);

    private _enableSubmitButtons = () => {
        const buttons = this._form.querySelectorAll('sbk-button[type="submit"]');
        buttons.forEach(btn => {
            (btn as SbkButton).disabled = false;
        });
    };

    private _disableSubmitButtons = () => {
        const buttons = this._form.querySelectorAll('sbk-button[type="submit"]');
        buttons.forEach(btn => {
            (btn as SbkButton).disabled = true;
        });
    };


    _updateRepeatableElements = (e: Event) => {
        const event = <CustomEvent> e;
        const elementToDelete = this._form.querySelector(`[id="${event.detail.id}"]`)
        elementToDelete && elementToDelete.remove();
        const removedEvent = new CustomEvent('repeatable-container-removed');
        this._form.dispatchEvent(removedEvent);
    }

    _onSubmit(event: SubmitEvent) {
        const form = <HTMLFormElement> event.target;
        this.trackMatomoSelectEvents(form);
        const invalidElements = SbkForm.validate(form);
        if (invalidElements.length) {
            event.preventDefault();
            const firstInvalidElement = invalidElements[0];
            firstInvalidElement.focus({preventScroll: true});
            firstInvalidElement.scrollIntoView({behavior: 'smooth', block: "start", inline: "nearest"});
        } else if (!(event.submitter?.dataset.preventDisable)) {
            this._disableSubmitButtons();
        }
    }

    static validate(form: HTMLFormElement): LitElement[] {
        let invalidElements: LitElement[] = [];
        const alreadyInvalidElements: NodeListOf<LitElement> = form.querySelectorAll('[invalid]');
        alreadyInvalidElements.forEach((element) => {
            if (element.offsetParent !== null) {
                invalidElements = [...invalidElements, element];
            }
        })
        const inputs: NodeListOf<LitElement> = form.querySelectorAll('sbk-input, :not(sbk-checkbox-group) > sbk-checkbox, sbk-checkbox-group, sbk-radio-group, sbk-textarea, sbk-select, sbk-datepicker, sbk-autocomplete-input-zip, sbk-autocomplete-input-city, sbk-autocomplete-input-street, sbk-file-upload')
        for (let i = 0; i < inputs.length; i++) {
            const element = inputs[i];
            if (element.offsetParent !== null && (element instanceof SbkInput ||
                element instanceof SbkCheckbox ||
                element instanceof SbkCheckboxGroup ||
                element instanceof SbkRadioGroup ||
                element instanceof SbkSelect ||
                element instanceof SbkDatepicker ||
                element instanceof SbkAutocompleteInputZip ||
                element instanceof SbkAutocompleteInputCity ||
                element instanceof SbkAutocompleteInputStreet ||
                element instanceof SbkTextarea ||
                element instanceof SbkFileUploadComponent)) {
                if (invalidElements.includes(element)) {
                    continue;
                }
                const validators = element.getValidators();
                if (element instanceof SbkSelect) {
                    const value = element.multiple ? element.selectedOptions.map(option => option.value).join(',')  // For multiselect, join selected values
                        : element.selectedOption.value;
                    element.handleErrors(validators, value);
                } else if (element instanceof SbkCheckboxGroup ||
                    element instanceof SbkCheckbox ||
                    element instanceof SbkRadioGroup) {
                    element.handleErrors(validators, '');
                } else if (element instanceof SbkDatepicker) {
                    element.handleErrors(validators, element.datepickerValue);
                } else {
                    element.handleErrors(validators, element.value ?? '');
                }
                if (element.invalid) {
                    invalidElements = [...invalidElements, element];
                }
            }
        }
        return invalidElements;
    }

    trackMatomoSelectEvents(form: HTMLFormElement) {
        const selects = form.querySelectorAll('sbk-select[data-matomo-track-select-event]');

        selects.forEach((element) => {
            const select = element as SbkSelect;
            const labelEl = form.querySelector(`label[for="${select.id}"]`);
            let labelText = labelEl?.textContent?.trim();

            if (!labelText) {
                labelText = select.getAttribute('label')?.trim() ?? select.name;
            }

            const category = labelText + ' - ' + window.location.pathname;
            let selected: string;

            if (select.multiple) {
                selected = select.selectedOptions.map(opt => opt.label).join(', ') || '–';
            } else {
                selected = select.selectedOption?.label ?? '–';
            }

            const action = `${labelText}: ${selected}`;
            _paq.push(['trackEvent', category, action, 'Form Selection']);
        });
    }

    _submitForm() {
        const form: HTMLFormElement | null = this.querySelector('form')
        form?.submit()
    }
}
